import { Ride, RideService, useCancellablePromise, useRides } from '@geovelo-frontends/commons';
import { Add } from '@mui/icons-material';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, navigate } from 'gatsby';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../components/button';
import ConfirmDialog from '../../components/confirm-dialog';
import EmptyState from '../../components/empty-state';
import { EditIcon, FavoriteIcon } from '../../components/icons';
import Seo from '../../components/seo';
import { AppContext } from '../../context';
import useQueryParams from '../../hooks/query-params';
import LeftPanelLayout from '../../layouts/app-content/left-panel';
import { TPageProps } from '../../page-props';
import CreateDialog from '../rides/create-dialog';
import RidesList from '../rides/rides-list';

function UserRidesPage(props: TPageProps): JSX.Element {
  const [rides, setRides] = useState<Ride[]>();
  const { path, location } = props;
  const { get: getQueryParams, update: updateQueryParams } = useQueryParams(location.search);
  const [selectedTab, selectTab] = useState<'favorites' | 'created'>(() => {
    const { tab } = getQueryParams();

    return tab === 'created' ? 'created' : 'favorites';
  });
  const [rideToRemove, setRideToRemove] = useState<Ride | null>(null);
  const [removeDialogOpen, openRemoveDialog] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [createDialogOpen, openCreateDialog] = useState(false);
  const {
    map: { current: map, zoom },
    user: { current: currentUser, rides: userRides },
    ride: { map: ridesMap },
    actions: { getUserRides },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const {
    init: initRidesOnMap,
    update: updateRidesMarkers,
    clear: clearRidesMarkers,
  } = useRides(map, theme, rides, undefined, true, {
    onClick: (ride) => {
      if (ride) navigate(`/${currentLanguage}/rides/${ride.id}`);
    },
  });
  const { cancellablePromise, cancelPromises } = useCancellablePromise();

  useEffect(() => {
    if (map) {
      initRidesOnMap();
    }

    return () => clearRidesMarkers();
  }, [map]);

  useEffect(() => {
    updateQueryParams({ tab: selectedTab });
  }, [selectedTab]);

  useEffect(() => {
    if (currentUser === null) navigate(`/${currentLanguage}/rides`);
  }, [currentUser]);

  useEffect(() => {
    if (selectedTab === 'favorites' && currentUser && !userRides) getUserRides();
    if (selectedTab === 'created') {
      if (!currentUser) selectTab('favorites');
      else getCreatedRides();
    }

    return () => cancelPromises();
  }, [currentUser, selectedTab]);

  useEffect(() => {
    return () => setRides(undefined);
  }, [selectedTab]);

  useEffect(() => {
    if (selectedTab === 'favorites') setRides(userRides?.map(({ rideId }) => ridesMap[rideId]));
  }, [selectedTab, userRides]);

  useEffect(() => {
    if (map && rides) updateRidesMarkers();
    else clearRidesMarkers();
  }, [map, zoom, rides]);

  async function getCreatedRides() {
    try {
      const { rides: createdRides } = await cancellablePromise(
        RideService.getRides({
          page: 1,
          pageSize: 100,
          orderBy: 'id',
          order: 'desc',
          query:
            '{id, title, geo_point_a, geo_point_a_title, geo_point_b, geo_point_b_title, geo_point_center, duration, vertical_gain, route_duration, distance, difficulty, icon, photos{thumbnail, thumbnailSquare}}',
          edition: true,
        }),
      );

      setRides(createdRides);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleRemoveConfirmDialogOpen(ride: Ride) {
    setRideToRemove(ride);
    openRemoveDialog(true);
  }

  async function handleRemove(confirmed: boolean) {
    if (confirmed && rideToRemove?.id) {
      setRemoving(true);

      try {
        await RideService.removeRide(rideToRemove.id);

        enqueueSnackbar(t('geovelo.rides.detail.remove_dialog.success'));

        if (rides) {
          const createdRides = [...rides];
          createdRides.splice(
            createdRides.findIndex(({ id }) => id === rideToRemove.id),
            1,
          );
          setRides(createdRides);
          openRemoveDialog(false);
          setTimeout(() => setRideToRemove(null), theme.transitions.duration.leavingScreen);
        }
      } catch (err) {
        enqueueSnackbar(t('geovelo.rides.detail.remove_dialog.server_error'), { variant: 'error' });
      }

      setRemoving(false);
    } else {
      openRemoveDialog(false);
    }
  }

  if (currentUser === null) return <></>;

  return (
    <>
      <Seo title={`${t('geovelo.rides.navigation.my_rides')}`} {...props} />
      <LeftPanelLayout
        backRoute={`/${currentLanguage}/rides`}
        title={t('geovelo.rides.navigation.my_rides')}
        {...props}
      >
        <Box display="flex" flexDirection="column" flexGrow={1} gap={3} paddingTop={3}>
          <Box display="flex" gap={2} paddingX={3}>
            <Button
              color={selectedTab === 'favorites' ? 'primary' : 'inherit'}
              onClick={() => selectTab('favorites')}
              startIcon={<FavoriteIcon />}
              variant="outlined"
            >
              {t('geovelo.rides.navigation.favorites')}
            </Button>
            <Button
              color={selectedTab === 'created' ? 'primary' : 'inherit'}
              onClick={() => selectTab('created')}
              startIcon={<EditIcon />}
              variant="outlined"
            >
              {t('geovelo.rides.navigation.created')}
            </Button>
          </Box>
          {selectedTab === 'favorites' && (
            <Box display="flex" flexDirection="column" flexGrow={1}>
              {rides && rides.length === 0 ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  flexGrow={1}
                  justifyContent="center"
                  paddingX={3}
                  paddingY={2}
                >
                  <EmptyState
                    action={
                      <Button
                        color="primary"
                        component={Link}
                        size="small"
                        to={`/${currentLanguage}/rides`}
                        variant="contained"
                      >
                        {t('geovelo.actions.show_rides')}
                      </Button>
                    }
                    text={t('geovelo.favorites.rides.empty_state_description')}
                    title={t('geovelo.favorites.rides.empty_state')}
                  />
                </Box>
              ) : (
                <>
                  <Box paddingX={3}>
                    <Typography variant="body2">
                      {rides ? (
                        t('geovelo.rides.count', { count: rides.length })
                      ) : (
                        <Skeleton variant="text" width={200} />
                      )}
                    </Typography>
                  </Box>
                  <RidesList path={path} rides={rides} />
                </>
              )}
            </Box>
          )}
          {selectedTab === 'created' && (
            <Box display="flex" flexDirection="column" flexGrow={1}>
              {rides && rides.length === 0 ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  flexGrow={1}
                  justifyContent="center"
                  paddingX={3}
                  paddingY={2}
                >
                  <EmptyState
                    action={
                      <Button
                        color="primary"
                        onClick={() => openCreateDialog(true)}
                        size="small"
                        startIcon={<Add />}
                        variant="contained"
                      >
                        {t('geovelo.rides.actions.create')}
                      </Button>
                    }
                    text={t('geovelo.created_rides.empty_state_description')}
                    title={t('geovelo.created_rides.empty_state')}
                  />
                </Box>
              ) : (
                <>
                  <Box paddingX={3}>
                    <Typography variant="body2">
                      {rides ? (
                        t('geovelo.rides.count', { count: rides.length })
                      ) : (
                        <Skeleton variant="text" width={200} />
                      )}
                    </Typography>
                  </Box>
                  <RidesList
                    disableFavorites
                    onRemove={handleRemoveConfirmDialogOpen}
                    path={path}
                    rides={rides}
                  />
                </>
              )}
            </Box>
          )}
        </Box>
      </LeftPanelLayout>
      <ConfirmDialog
        dangerous
        cancelLabel={t('geovelo.rides.detail.remove_dialog.cancel')}
        confirmLabel={t('geovelo.rides.detail.remove_dialog.confirm')}
        loading={removing}
        onClose={handleRemove}
        open={removeDialogOpen}
        title={t('geovelo.rides.detail.remove_dialog.title')}
      />
      <CreateDialog onClose={() => openCreateDialog(false)} open={createDialogOpen} />
    </>
  );
}

export default UserRidesPage;
